// =============================================================================
//  dataSourceIdentity.js — the ONE central mapping from any "Data Source"
//  registry key a Family session might click on (PageBuilder.jsx's own
//  "Templates & Sources de données" modal: SAMPLE_DATASETS' bare keys like
//  "about"/"seller", or liveDatasets' "cp_<pageKey>" live entries) to the
//  REAL, canonical public-page identity (`content.<pageKey>`) the rest of
//  the app (SystemTemplatesPanel.jsx, howToStep.jsx, Layout/Footer.js) uses.
//
//  WHY THIS EXISTS (fix-400 + system-template-selection mission): "About" in
//  that modal and "About Us" (`content.aboutUs`) LOOK like the same thing to
//  Family, but were, before this fix, two completely separate identities --
//  Save/Link/Select-Template on the modal's "About" card never touched the
//  real public About Us page at all. Every action that can affect a real
//  Content Page (Save, Edit, Link, Select System/Page Template, Template
//  Active, Priority) MUST resolve through this SAME function first, so it
//  always lands on the SAME canonical key -- never "Save uses dataset.about
//  but Link uses content.aboutUs" (task spec §8's own explicit warning).
//
//  A registry key that has NO real Content Page counterpart (e.g. "store",
//  "caseStudies", "projectInvest" -- different pageModes entirely, or
//  "investContent" -- no confident 1:1 real-page match) is intentionally
//  LEFT AS `dataset.<key>`: a purely internal/demo binding, never claiming
//  to be a real public page (task spec's own answer, point 5).
// =============================================================================

import { CONTENT_PAGE_KEYS } from "../../pages/contentPageUtils";

// Known aliases from SAMPLE_DATASETS' own (shorter, French-authored) keys to
// the real CONTENT_PAGE_KEYS they represent. Deliberately a short, explicit,
// reviewable list -- extend it here (and ONLY here) if SAMPLE_DATASETS ever
// grows another entry that represents a real Content Page.
export const CONTENT_PAGE_ALIAS_MAP = {
  about: "aboutUs",
  seller: "becomeSeller",
};

// `registryKey` is whatever key the "Templates & Sources de données" modal
// already uses (bare SAMPLE_DATASETS key, or a live "cp_<pageKey>" key) --
// never re-typed/guessed at each call site.
export const resolveCanonicalDataSourceKey = (registryKey) => {
  if (typeof registryKey !== "string" || !registryKey) return "";

  // Already canonical (e.g. a caller re-resolving a key it already holds).
  if (registryKey.startsWith("content.")) return registryKey;

  // Live content datasets are ALWAYS "cp_<realPageKey>" -- buildLanguageContentPageDatasets
  // (contentPageUtils.js) builds this key directly from CONTENT_PAGE_KEYS,
  // so a straight prefix-strip is exact, never a guess.
  if (registryKey.startsWith("cp_")) {
    const pageKey = registryKey.slice(3);
    if (CONTENT_PAGE_KEYS.includes(pageKey)) return `content.${pageKey}`;
  }

  // Static SAMPLE_DATASETS demo key with a known real-page alias.
  const aliasedPageKey = CONTENT_PAGE_ALIAS_MAP[registryKey];
  if (aliasedPageKey && CONTENT_PAGE_KEYS.includes(aliasedPageKey)) {
    return `content.${aliasedPageKey}`;
  }

  // No real Content Page counterpart -- stays an internal/demo dataset
  // pointer (task spec's own point 5).
  return `dataset.${registryKey}`;
};

// True when a registry key resolves to a REAL public Content Page (as
// opposed to a purely internal/demo dataset binding) -- lets the UI decide
// whether "Select System Template + Lier" should even be offered, since
// linking a template only means something for a real public page.
export const isRealContentPageDataSource = (registryKey) =>
  resolveCanonicalDataSourceKey(registryKey).startsWith("content.");
